// =========================
// Dashboard — BL-039 (Investor Intelligence Dashboard, Phase 1)
//
// Authenticated investor command center. Fetches the user's own dashboard
// signals from /get-dashboard-metrics (user-scoped, read-only, derived data
// only) and renders them via DashboardView. Loading / error / empty states
// are handled here; all presentation + the ADR-044 signal framing live in
// DashboardView.
// =========================

import React, { useEffect, useState, useCallback } from 'react';
import { fetchDashboardMetrics } from '../lib/dashboardMetrics';
import { useSubscription } from '../context/SubscriptionContext';
import DashboardView from '../components/dashboard/DashboardView';
import MarketOpportunitySignals from '../components/dashboard/MarketOpportunitySignals';
import { useFeature } from '../context/FeatureContext';
import { dashboardLoaded } from '../lib/onboardingFunnelAnalytics';

export default function Dashboard() {
  // BL-011 — Opportunity Intelligence panel (cross-user, derived, signal-not-
  // advice). PLATFORM-003: visibility is RUNTIME-controlled via the feature
  // registry (get-feature-state), not a build-time env var. Fail-closed to dark
  // until the Admin control plane enables it for this user.
  const opportunitySignalsEnabled = useFeature('opportunity_signals_enabled');

  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Profile drives advisory entitlement hints only (server is the boundary).
  const profile = useSubscription()?.profile || null;

  // BL-017 Opportunity Qualification is build-time feature-flagged (same
  // convention as the /qualify route in App.js and the nav item in Layout.js).
  // In production the flag is OFF, so the route is never mounted — the dashboard
  // must not surface /qualify CTAs there or they navigate to a blank screen.
  const qualificationEnabled = process.env.REACT_APP_QUALIFICATION_ENABLED === 'true';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await fetchDashboardMetrics();
    if (loadError) {
      setError(loadError.message || 'Could not load your dashboard.');
      setMetrics(null);
    } else {
      setMetrics(data);
    }
    setLoading(false);
    // Onboarding funnel — Dashboard Arrival (funnel step 5). PII-safe flag only;
    // fires on each successful load. track() env-tags via BL-025.
    if (!loadError) dashboardLoaded({ has_metrics: !!data });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div style={{ padding: 'var(--space-5)', color: 'var(--color-steel-lt)' }}>Loading…</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 'var(--space-5)' }}>
        <div
          role="alert"
          style={{
            padding: 'var(--space-4) var(--space-5)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-danger)',
            background: 'var(--color-danger-ghost)',
            color: 'var(--color-danger-lt)',
            fontWeight: 'var(--weight-semibold)',
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardView metrics={metrics} profile={profile} qualificationEnabled={qualificationEnabled} />
      {opportunitySignalsEnabled ? (
        <div style={{ padding: '0 var(--space-5) var(--space-5)' }}>
          <MarketOpportunitySignals />
        </div>
      ) : null}
    </>
  );
}
